import { appConfiguration } from "./appConfiguration.ts";
import { ScanTypeCreateDto, setBeamlineParameterOrder, ScanTypeDto,
  createScanType, updateScanType, deleteScanType,
  EditAsChoice, ParameterDto, ParameterCreateDto, createParameter, updateParameter, deleteParameter,
  ParameterSettingsDto, ParameterSettingsCreateDto, createParameterSettings, deleteParameterSettings
  } from './api/api.ts';
import { ResponseWrapper } from './api/basicApi.ts';
import { ChangeSet, EditQueueEntry, UndoHistory, UndoHistoryEntry } from "./undoHistory.ts";
import { ValidatorStep, validateValueWithValidatorStep, validatorStepToDescription } from "./validation.ts";
import { Uid, sortWithNumberParsing } from "./utils.tsx";

type ScanTypeSlug = string & { __brand: 'Scan Type Slug' };

type ParameterSlug = string & { __brand: 'Scan Parameter Slug' };


//
// ScanTypes and their Parameters, and the intermediate ParameterSettings that
// link them together, are all defined belonging to a particular Beamline.
// The classes used here are meant to use records from one Beamline at a time.
// (This is supplied as a BeamlineDetailsDto).
// This allows us to make a few assumptions, the main one being:
// All Parameter names will be unique.
//


interface ParameterChoice {
  name: string;
  description: string;
}


// An enhanced version of a ParameterDto
class Parameter {
  id: Uid;
  slug: ParameterSlug;
  name: string;
  description: string;
  slugBeamline: string;
  order: number;
  idDerivedFrom?: Uid;
  standardDefault?: string;
  enforceUniqueInSet: boolean;
  autoGenerateInterval?: number;
  editAs: EditAsChoice;
  choices?: ParameterChoice[];
  // Validates the input. Any return value other than null is considered an error and displayed as an error message.
  validatorFunction?: (value:string) => null | string;
  validatorObject?: ValidatorStep;
  schemaIdentifier?: string;
  lastModified: string;

  constructor(dto: ParameterDto) {

    let validatorFunction:(v: string) => string | null = (v:string) => { return null; };
    // The validafor function comes from the server as JSON object.
    // Details are in "validation.ts".
    if (dto.validation) {
      this.validatorObject = dto.validation as ValidatorStep;
      // Build a validator function for this parameter that returns the description of the requirements
      // if the value is invalid, otherwise null.
      validatorFunction = (v:string) => {
        try {
          const result = validateValueWithValidatorStep(v, dto.validation as ValidatorStep);
          if (!result) {
            return validatorStepToDescription(dto.validation as ValidatorStep);
          } else {
            return null;
          }
        } catch (e) {
          appConfiguration.logger(`Error validating parameter ${dto.slug} with value ${v}: ${e}`);
          return "Invalid value";
        }
      }
      this.validatorFunction = validatorFunction;
    }

    let choices = undefined;
    // The choices field comes from the server as JSON, but we want it to be an array of objects with name and description fields.
    if (dto.choices) {
      if (Array.isArray(dto.choices) && dto.choices.every( (c) => typeof c.name === "string" && typeof c.description === "string" )) {
        choices = dto.choices as ParameterChoice[];
      } else {
        appConfiguration.logger(`Invalid choices field for parameter ${dto.slug}: ${dto.choices}`);
      }
    }

    this.id = dto.id as Uid;
    this.slug = dto.slug as ParameterSlug;
    this.slugBeamline = dto.slug_beamline;
    this.name = dto.name;
    this.description = dto.description;
    this.idDerivedFrom = dto.id_derived_from as Uid | undefined;
    this.order = dto._order;
    this.standardDefault = dto.standard_default;
    this.enforceUniqueInSet = dto.enforce_unique_in_set;
    this.autoGenerateInterval = dto.auto_generate_interval;
    this.editAs = dto.edit_as || EditAsChoice.Inline;
    this.choices = choices;
    this.schemaIdentifier = dto.schema_identifier;
    this.lastModified = dto.date_last_modified;
  }

  asDto(): ParameterDto {
    return {
      id: this.id,
      slug: this.slug,
      name: this.name,
      description: this.description,
      slug_beamline: this.slugBeamline,
      _order: this.order,
      id_derived_from: this.idDerivedFrom,
      standard_default: this.standardDefault,
      enforce_unique_in_set: this.enforceUniqueInSet,
      auto_generate_interval: this.autoGenerateInterval,
      edit_as: this.editAs,
      choices: this.choices || null,
      validation: this.validatorObject || null,
      schema_identifier: this.schemaIdentifier,
      date_last_modified: this.lastModified,
    }
  }

  clone():Parameter {
    const asDto = this.asDto();
    return new Parameter(asDto);
  }


  async writeToServer():Promise<ResponseWrapper<ParameterDto>> {
    return updateParameter(this.asDto());
  }
}


// An enhanced version of a ScanTypeDto.
class ScanType {
  id: Uid;
  slug: ScanTypeSlug;
  name: string;
  slugBeamline: string;
  description: string;
  slugDerivedFrom?: ScanTypeSlug;
  deprecated: boolean;
  // This should always match the keys of the parametersById map.
  // It's redundant, but it's easier to use this than cast an iterator of object keys into an array every time.
  parameterIds: Uid[];
  parameterSettingsById: Map<Uid, ParameterSettingsDto>;
  dateLastModified: string;
  dateRecordCreated: string;

  constructor(dto: ScanTypeDto) {
    const parameterSettingsById = dto.parameters.reduce( (map, ps) => {
      map.set(ps.id_parameter, ps);
      return map;
    }, new Map<Uid, ParameterSettingsDto>() );
    const parameterIds = Array.from(parameterSettingsById.keys());

    this.id = dto.id as Uid;
    this.slug = dto.slug as ScanTypeSlug;
    this.name = dto.name;
    this.slugBeamline = dto.slug_beamline;
    this.description = dto.description || "";
    this.slugDerivedFrom = dto.slug_derived_from as ScanTypeSlug | undefined;
    this.deprecated = dto.deprecated;
    this.parameterIds = parameterIds;
    this.parameterSettingsById = parameterSettingsById;
    this.dateLastModified = dto.date_last_modified;
    this.dateRecordCreated = dto.date_record_created;
  }

  asDto(): ScanTypeDto {
    const parameterSettings = Array.from(this.parameterSettingsById.values());
    return {
      id: this.id,
      slug: this.slug,
      name: this.name,
      slug_beamline: this.slugBeamline,
      description: this.description,
      slug_derived_from: this.slugDerivedFrom,
      deprecated: this.deprecated,
      parameters: parameterSettings,
      date_last_modified: this.dateLastModified,
      date_record_created: this.dateRecordCreated,
    };
  }

  clone():ScanType {
    const asDto = this.asDto();
    return new ScanType(asDto);
  }


  async writeToServer():Promise<ResponseWrapper<ScanTypeDto>> {
    return updateScanType(this.asDto());
  }


  async deleteSelfFromServer(): Promise<ResponseWrapper<boolean>> {
    // Delete all the ParameterSettings first
    const deleteSettingsCalls = Array.from(this.parameterSettingsById.values()).map((s) => deleteParameterSettings(s.id));
    const deleteSettingsResults = await Promise.all(deleteSettingsCalls);
    if (deleteSettingsResults.some((r) => !r.success)) {
      return { success: false, message: "Failed to delete one or more parameter settings." };
    }
    const deleteSetResult = await deleteScanType(this.slug);
    if (!deleteSetResult.success) {
      return { success: false, message: "Failed to delete the scan type itself." };
    }
    return { success: true, response: true };
  }
}


class ScanTypeCatalog {

  typesBySlug: Map<ScanTypeSlug, ScanType>;
  typeSlugsByName: Map<string, ScanTypeSlug>;
  typeSlugsInDisplayOrder: ScanTypeSlug[];
  parametersById: Map<Uid, Parameter>;
  parametersBySlug: Map<ParameterSlug, Parameter>;
  parameterIdsInDisplayOrder: Uid[];
  // Undo/redo history tracker
  history: UndoHistory;

  constructor(parameters: ParameterDto[], scanTypes: ScanTypeDto[]) {
    this.typesBySlug = new Map<ScanTypeSlug, ScanType>();
    this.typeSlugsByName = new Map<string, ScanTypeSlug>();
    this.typeSlugsInDisplayOrder = [];
    this.parametersById = new Map<Uid, Parameter>();
    this.parametersBySlug = new Map<ParameterSlug, Parameter>();
    this.parameterIdsInDisplayOrder = [];
    this.history = new UndoHistory();

    this.reconstruct(parameters, scanTypes);
  }


  reconstruct(parameters: ParameterDto[], scanTypes: ScanTypeDto[]) {
    var parametersById = new Map<Uid, Parameter>();
    var parametersBySlug = new Map<ParameterSlug, Parameter>();
    parameters.forEach((p) => {
      const param: Parameter = new Parameter(p);
      parametersById.set(p.id, param);
      parametersBySlug.set(p.slug as ParameterSlug, param);
    });

    var typesBySlug = new Map<ScanTypeSlug, ScanType>();
    var typeSlugsByName = new Map<string, ScanTypeSlug>();
    scanTypes.forEach((t) => {
      const scanType = new ScanType(t);
      typeSlugsByName.set(t.name, scanType.slug);
      typesBySlug.set(t.slug as ScanTypeSlug, scanType);
    });

    const typeSlugsInDisplayOrder = Array.from(typesBySlug.values()).sort((a, b) => {
      return sortWithNumberParsing(a.name, b.name);
    }).map((t) => t.slug );

    const parameterIdsInDisplayOrder = parameters.map((p) => p.id).sort((a, b) => {
      const paramA = parametersById.get(a)!;
      const paramB = parametersById.get(b)!;
      if (paramA.order != paramB.order) {
        return paramA.order - paramB.order;
      } else {
        // If the order field is the same, use name instead.
        return sortWithNumberParsing(paramA.name, paramB.name);
      }
    });

    this.typesBySlug = typesBySlug;
    this.typeSlugsByName = typeSlugsByName;
    this.typeSlugsInDisplayOrder = typeSlugsInDisplayOrder;
    this.parametersById = parametersById;
    this.parametersBySlug = parametersBySlug;
    this.parameterIdsInDisplayOrder = parameterIdsInDisplayOrder;
  }


  // This method assumes that the two main structures in the object have changed
  // (parametersById and typesBySlug), and rebuilds all other structures based on those.
  // Used when a do/undo/redo operation makes an alteration that should be propagated.
  reconstructFromInternalValues() {
    const parameters = Array.from(this.parametersById.values()).map((p) => { return p.asDto(); });
    const scanTypes = Array.from(this.typesBySlug.values()).map((p) => { return p.asDto(); });
    this.reconstruct(parameters, scanTypes);
  }


  // Add the given Parameter objects,
  // making an undo history entry along the way.
  // This is only called by the "add" dialog,
  // and it generates the relevant records on the server before calling.
  addWithHistory(input: Parameter[]) {
    const h = new UndoHistoryEntry();

    // Every given Parameter is a new change in forward history
    input.forEach((i) => h.forwardAdd(i));
    // We used to do a "backwardDelete" as well, but now we don't remove
    // new Parameters when we go back in the undo history because we consider
    // deletion to be a permanent operation.  The new parameters remain even
    // if we undo changes to the old ones.
    // Event construction is complete.
    this.history.do(h);

    // Now that we've updated undo/redo history, write the changes.
    input.forEach((i) => this.parametersById.set(i.id, i));

    // We need to rebuild ALL the data structures in this ScanTypeCatalog
    // now, because changing the slug, name, or sort order of a Parameter
    // can change all of them.
    this.reconstructFromInternalValues();
  }


  // Replace the given Parameter objects,
  // making an undo history entry along the way.
  replaceWithHistory(input: Parameter[]) {
    const h = new UndoHistoryEntry();
    const currentSet = this.parametersById;

    // Every given Parameter is a new change in forward history
    input.forEach((i) => h.forwardAdd(i));
    // Every Parameter in the existing set that's replaced
    // should be preserved and restored if we go backward in history (undo)
    const verifiedExisting = input.filter((i) => currentSet.has(i.id));
    verifiedExisting.forEach((i) => h.backwardAdd(currentSet.get(i.id)!));
    // Event construction is complete.
    this.history.do(h);

    // Now that we've updated undo/redo history, write the changes.
    input.forEach((i) => currentSet.set(i.id, i));

    // We need to rebuild ALL the data structures in this ScanTypeCatalog
    // now, because changing the slug, name, or sort order of a Parameter
    // can change all of them.
    this.reconstructFromInternalValues();
  }


  undo(): ChangeSet | null {
    const edit = this.history.undo();
    if (!edit) { return null; }

    const currentSet = this.parametersById;
    edit.additions.forEach((a) => {
      const s = a as Parameter;
      currentSet.set(s.id, s)
    });
    // We used to handle removing anything in a "deletions" field as well,
    // but now we consider deletions permanent and a manual prcoess only,
    // so we don't make them part of undo history.
    this.reconstructFromInternalValues();
    return edit;
  }


  redo(): ChangeSet | null {
    const edit = this.history.redo();
    if (!edit) { return null; }

    const currentSet = this.parametersById;
    edit.additions.forEach((a) => {
      const s = a as Parameter;
      currentSet.set(s.id, s)
    });
    this.reconstructFromInternalValues();
    return edit;
  }


  canRedo(): boolean {
    return this.history.canRedo();
  }

  canUndo(): boolean {
    return this.history.canUndo();
  }


  getPendingChanges(): EditQueueEntry | null {
    return this.history.getPendingChanges();
  }

  // Pulls items off the beginning of the edit queue
  // up to and including the item with the given index,
  // or just clears the whole queue if that index isn't present.
  catchUpToEdit(index:number) {
    return this.history.catchUpToEdit(index);
  }

  async catchUpEdits(): Promise<ResponseWrapper<boolean>> {
    const thisSet = this;
    const changes = this.getPendingChanges();
    let errors:string[] = [];
    if (changes) {
      let saveCalls = changes.changes.additions.map((e) => {
        const c = e as Parameter;
        return c.writeToServer();
      });
      // Any set of changes could have also altered the ordering
      // of the parameters, so we also need to make a call to
      // assert that ordering.  Almost all the time it will be redundant.
      saveCalls.push( setBeamlineParameterOrder({
        // We can take the beamline slug from any parameter, since they should all be the same.
        slug: thisSet.parametersById.values().next().value!.slugBeamline,
        order_ids: thisSet.parameterIdsInDisplayOrder,
      }) );

      Promise.all(saveCalls).then((responses) => {
        if (responses.every((r) => r.success)) {
          thisSet.catchUpToEdit(changes.index);
        } else {
          responses.forEach((r) => {
            if (!r.success && r.message) {
              errors.push(r.message);
            }
          });
        }
      });
      if (errors.length > 0) {
        return { success: false, message: "Could not catch up edits: " + errors.join(', ') };
      }
    }
    return { success: true, response: true };
  }


  // Deletes all the Samples in a Set from the server, deletes the Set, and removes it from this instance.
  async deleteScanTypeFromServerAndRemove(slug: ScanTypeSlug): Promise<ResponseWrapper<boolean>> {
    const scanTypeToDelete = this.typesBySlug.get(slug);
    if (!scanTypeToDelete) { return { success: false, message: "Scan Type not found." }; }
    const result = await scanTypeToDelete.deleteSelfFromServer();
    if (result.success) {
      this.typesBySlug.delete(slug);
      this.reconstructFromInternalValues();
    } else {
      console.error("Failed to delete scan type: " + result.message);
    }
    return result;
  }


  // Deletes the given ParameterSettings from the server, then removes them from the affected ScanTypes.
  async deleteParameterSettingsFromServerAndRemove(ids: Uid[]): Promise<ResponseWrapper<boolean>> {

    const parameterSettingsIdsToDelete = new Set(ids);
    // Turn the current state of this object back into DTOs. Easier to manipulate.
    const parameters = Array.from(this.parametersById.values()).map((p) => { return p.asDto(); });
    const scanTypes = Array.from(this.typesBySlug.values()).map((p) => { return p.asDto(); });

    // Filter out the ParameterSettings to delete from all the ScanTypes.
    // This will result in records that mimic what we intend to perform on the server.
    const scanTypesWithRemovedParameterSettings = scanTypes.map((s) => {
      const newParameterSettings = s.parameters.filter((ps) => !parameterSettingsIdsToDelete.has(ps.id));
      return { ...s, parameters: newParameterSettings };
    });

    // Make an array of all the ParameterSettings that need to be deleted, turn them into calls, and execute them in parallel.
    // Not a risky operation because ParameterSettings records are not relied on by any other database structure.
    const parameterSettingsToDelete = scanTypes.flatMap((s) => s.parameters.filter((ps) => parameterSettingsIdsToDelete.has(ps.id)));
    const deleteSettingsCalls = parameterSettingsToDelete.map((s) => deleteParameterSettings(s.id));
    const deleteSettingsResults = await Promise.all(deleteSettingsCalls);
    if (deleteSettingsResults.some((r) => !r.success)) {
      return { success: false, message: "Failed to delete one or more parameter settings." };
    }

    this.reconstruct(parameters, scanTypesWithRemovedParameterSettings);
    return { success: true, response: true };
  }


  // Deletes all the Samples in a Set from the server, deletes the Set, and removes it from this instance.
  async deleteParametersFromServerAndRemove(ids: Uid[]): Promise<ResponseWrapper<boolean>> {

    const parameterIdsToDelete = new Set(ids);
    // Turn the current state of this object back into DTOs. Easier to manipulate.
    const parameters = Array.from(this.parametersById.values()).map((p) => { return p.asDto(); });
    const scanTypes = Array.from(this.typesBySlug.values()).map((p) => { return p.asDto(); });

    // Filter out the Parameters to delete, and remove any ParameterSettings that reference them in the ScanTypes.
    // This will result in records that mimic what we intend to perform on the server.
    const parametersToKeep = parameters.filter((p) => !parameterIdsToDelete.has(p.id));
    const scanTypesWithRemovedParameterSettings = scanTypes.map((s) => {
      const newParameterSettings = s.parameters.filter((ps) => !parameterIdsToDelete.has(ps.id_parameter));
      return { ...s, parameters: newParameterSettings };
    });

    // Make an array of all the ParameterSettings that need to be deleted, turn them into calls, and execute them in parallel.
    // Not a risky operation because ParameterSettings records are not relied on by any other database structure.
    const parameterSettingsToDelete = scanTypes.flatMap((s) => s.parameters.filter((ps) => parameterIdsToDelete.has(ps.id_parameter)));
    const deleteSettingsCalls = parameterSettingsToDelete.map((s) => deleteParameterSettings(s.id));
    const deleteSettingsResults = await Promise.all(deleteSettingsCalls);
    if (deleteSettingsResults.some((r) => !r.success)) {
      return { success: false, message: "Failed to delete one or more parameter settings." };
    }

    // Now delete the Parameters themselves.
    const deleteParameterCalls = ids.map((id) => deleteParameter(id));
    const deleteParameterResults = await Promise.all(deleteParameterCalls);
    if (deleteParameterResults.some((r) => !r.success)) {
      return { success: false, message: "Failed to delete one or more parameters." };
    }

    this.reconstruct(parametersToKeep, scanTypesWithRemovedParameterSettings);
    return { success: true, response: true };
  }


  // Creates a ScanType record using the given settings, and adds it to the ScanTypeCatalog.
  async createScanType(dto: ScanTypeCreateDto): Promise<ResponseWrapper<ScanTypeDto>> {
    const result = await createScanType(dto);
    if (result.success) {
      const newScanTypeDto = result.response!;
      const newScanType = new ScanType(newScanTypeDto);
      this.typesBySlug.set(newScanTypeDto.slug as ScanTypeSlug, newScanType);
      this.reconstructFromInternalValues();
    } else {
      console.error("Failed to create scan type: " + result.message);
    }
    return result;
  }


  // Creates a Parameter record using the given settings, and adds it to the ScanTypeCatalog.
  async createParameter(dto: ParameterCreateDto): Promise<ResponseWrapper<ParameterDto>> {
    const result = await createParameter(dto);
    if (result.success) {
      // Turn the current state of this object back into DTOs. Easier to manipulate.
      const parameters = Array.from(this.parametersById.values()).map((p) => { return p.asDto(); });
      const scanTypes = Array.from(this.typesBySlug.values()).map((p) => { return p.asDto(); });
      parameters.push(result.response!);
      this.reconstruct(parameters, scanTypes);
    } else {
      console.error("Failed to create scan type: " + result.message);
    }
    return result;
  }


  // Creates a ParameterSetting record using the given settings, and adds it to the appropriate Scan Type.
  async addParameterToScanType(dto: ParameterSettingsCreateDto): Promise<ResponseWrapper<ParameterSettingsDto>> {
    const scanType = this.typesBySlug.get(dto.slug_scan_type as ScanTypeSlug);
    if (!scanType) { return { success: false, message: "Scan Type not found." }; }
    const result = await createParameterSettings(dto);
    if (result.success) {
      scanType.parameterSettingsById.set(result.response!.id_parameter, result.response!);
      scanType.parameterIds.push(result.response!.id_parameter);
      this.reconstructFromInternalValues();
    } else {
      console.error("Failed to add scan type settings: " + result.message);
    }
    return result;
  }
}


export type {
  ParameterChoice,
  ParameterSlug,
  Parameter,
  ScanTypeSlug,
  ScanType,
};

export { ScanTypeCatalog }

